import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AmplitudeService, FeatureFlagService } from '@inyova/inyova-shared';
import { Customer } from '@inyova/models';
import { isInvestAccount } from '@inyova/utils';
import * as Sentry from '@sentry/angular';
import { NgxZendeskWebwidgetService } from 'ngx-zendesk-webwidget';
import { register as registerSwiperElements } from 'swiper/element/bundle';

import { Store } from '@ngrx/store';
import { firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import * as AppRootActions from './app.actions';
import * as fromAppRoot from './app.reducers';

import { AuthService } from '@shared/services/auth.service';
import { DeepLinksService } from '@shared/services/deep-links.service';
import { LanguageService, SELECTED_LANGUAGE } from '@shared/services/language.service';
import { SharedService } from '@shared/services/shared.service';
import { TrackingService } from '@shared/services/tracking.service';
import { getCookie } from '@shared/utils';

import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  styleUrls: ['./app.component.scss'],
  templateUrl: './app.component.html',
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  protected readonly onDestroy$ = new Subject<void>();
  authInitialized = false;
  loggedIn = false;
  activeHamburger = false;
  step: number;
  email: string;
  email_confirmed: boolean;
  first_name: string;
  zendesk_email: string;
  customer: Partial<Customer>;
  error = '';
  appLocation = 'ch';
  customerLanguage = 'en';
  referrer: string;
  currentLocation: string;
  editModeActive: boolean;
  currentUrl = '';

  customerAndCurrentAccountSub: Subscription;

  showAppPromo$: Observable<boolean> = this.store.select(fromAppRoot.showMobilePromo);
  showPromotions$: Observable<boolean> = this.store.select(fromAppRoot.showPromotionsDialog);

  constructor(
    private zendeskService: NgxZendeskWebwidgetService,
    private authService: AuthService,
    private store: Store,
    private trackingService: TrackingService,
    private languageService: LanguageService,
    private deepLinksService: DeepLinksService,
    private router: Router,
    private amplitudeService: AmplitudeService,
    private featureFlagService: FeatureFlagService,
    private sharedService: SharedService
  ) {
    registerSwiperElements();
    // TODO: 20.02.2026 Temporary disabled till we figure out the problem with the script
    // void this.zendeskService.initZendesk();
    Sentry.addEventProcessor((event) => {
      if (event.level === 'error' || event.level === 'fatal') {
        const errorValue = event.exception?.values?.[0];

        this.amplitudeService.trackEvent('App Error Occurred', {
          error_type: errorValue?.type ?? 'Unknown',
          error_message: (errorValue?.value ?? 'No message').substring(0, 1024),
          error_level: event.level,
          is_handled: errorValue?.mechanism?.handled ?? true,
          error_mechanism: errorValue?.mechanism?.type ?? 'unknown',
          monitoring_event_id: event.event_id,
          monitoring_project: environment.productType,
          monitoring_source: 'sentry'
        });
      }
      return event;
    });
    this.callAutopilot();
  }

  ngOnInit(): void {
    this.router.events.pipe(filter<NavigationEnd>((event) => event instanceof NavigationEnd)).subscribe((event) => {
      this.trackingService.trackAppNavigation(event);

      if (this.loggedIn) {
        this.deepLinksService.getSelectedAccountFromURL(event);
        this.trackingService.trackView(event.url);
        this.currentUrl = event.url;
      }
    });

    this.authService.isLoggedIn$.subscribe(async (isLoggedIn) => {
      if (!this.authInitialized) {
        this.authInitialized = true;
        this.loggedIn = isLoggedIn;
        return;
      }

      if (this.loggedIn === isLoggedIn) {
        return;
      }

      this.loggedIn = isLoggedIn;

      if (!this.loggedIn) {
        if (this.customerAndCurrentAccountSub) {
          this.customerAndCurrentAccountSub.unsubscribe();
          this.customerAndCurrentAccountSub = undefined;
        }
        void this.router.navigate(['/login']);
        this.amplitudeService.reset();
      } else {
        await firstValueFrom(this.featureFlagService.initialize());
        this.store.dispatch(AppRootActions.fetchABTests());
        this.store.dispatch(AppRootActions.fetchThirdParties({ service: '' }));
        this.store.dispatch(AppRootActions.showMobilePromo({ show: !getCookie('appPromoSeen') }));

        this.customerAndCurrentAccountSub = this.store
          .select(fromAppRoot.selectCustomerAndCurrentAccount)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe(({ customer, currentAccountDetail }) => {
            if (!customer || !customer.email || !customer.email.length) {
              return;
            }

            this.email = customer.email;
            this.first_name = `${customer.first_name} ${customer.last_name}`;
            this.appLocation = customer.app_location;
            this.customerLanguage = customer.language;
            this.email_confirmed = customer.email_confirmed;

            // keep it here because of email dependencies
            this.updateZendDeskSetup();

            this.step = currentAccountDetail.step_status;

            this.editModeActive = isInvestAccount(currentAccountDetail) && currentAccountDetail.edit_mode;

            // Set user context to get to know which user has the issue
            Sentry.setUser({
              id: customer.inyova_id.toString()
            });
            this.amplitudeService.setUserProperties(customer);
          });
      }
    });

    const urlParams = new URLSearchParams(location.search);
    const adminInUrl = urlParams.get('is_admin') !== null ? urlParams.get('is_admin') : '';
    if (adminInUrl === '1') {
      document.cookie = 'is_admin=true';
    }

    const lang = urlParams.get('lang');
    if (lang) {
      localStorage.setItem(SELECTED_LANGUAGE, lang);
    }
    this.languageService.setInitialAppLanguage();
  }

  updateZendDeskSetup(): void {
    // TODO: 20.02.2026 Temporary disabled till we figure out the problem with the script
    return;
    const isAdmin = getCookie('is_admin');
    if (environment.production && !isAdmin) {
      this.zendesk_email = this.email;
      this.callZendesk();
    } else {
      // user who is simulating email
      this.first_name = 'Lenka';
      this.zendesk_email = window.location.origin.includes('.ch') ? 'lenka@inyova.ch' : 'lenka@inyova.de';
      this.callZendesk();
    }
  }

  checkScroll(): void {
    if (this.activeHamburger) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }

  callZendesk(): void {
    const user = {
      name: this.first_name,
      email: this.zendesk_email,
      organization: 'Inyova Customers App'
    };

    const interval = setInterval(() => {
      if (typeof this.zendeskService.zE === 'undefined') {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.zendeskService.zE.setLocale(this.customerLanguage);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.zendeskService.zE.identify(user);
      clearInterval(interval);
    }, 700);
  }

  callAutopilot(): void {
    const isAdmin = getCookie('is_admin');
    if (!environment.production || isAdmin) {
      return;
    }
    // and when we simulate
    const autopilot1 = document.createElement('script');
    autopilot1.type = 'text/javascript';
    // CH  - "t="c7f5a024eb6f4a009824e886e98e0f869656427451134dc5a3324ca11710badf"
    // DE - "t="a9f3df4c047549c691a1af6095c2154c2e4ad7b9f05d4ebd8b99f210db972f93"
    let autoPilotID = 'c7f5a024eb6f4a009824e886e98e0f869656427451134dc5a3324ca11710badf';
    if (this.appLocation !== 'ch') {
      autoPilotID = 'a9f3df4c047549c691a1af6095c2154c2e4ad7b9f05d4ebd8b99f210db972f93';
    }

    autopilot1.innerHTML = `(function(o){var b="https://api.autopilothq.com/anywhere/",t="${autoPilotID}",a=window.AutopilotAnywhere={_runQueue:[],run:function(){this._runQueue.push(arguments);}},c=encodeURIComponent,s="SCRIPT",d=document,l=d.getElementsByTagName(s)[0],p="t="+c(d.title||"")+"&u="+c(d.location.href||"")+"&r="+c(d.referrer||""),j="text/javascript",z,y;if(!window.Autopilot) window.Autopilot=a;if(o.app) p="devmode=true&"+p;z=function(src,asy){var e=d.createElement(s);e.src=src;e.type=j;e.async=asy;l.parentNode.insertBefore(e,l);};y=function(){z(b+t+'?'+p,true);};if(window.attachEvent){window.attachEvent("onload",y);}else{window.addEventListener("load",y,false);}})({"app":true});`;

    const autopilot2 = document.createElement('script');
    autopilot2.type = 'text/javascript';
    autopilot2.innerHTML = `Autopilot.run("associate", "${this.email}");`;

    document.head.appendChild(autopilot1);
    document.head.appendChild(autopilot2);
  }

  launchZendesk() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.zendeskService.zE.activate();
  }

  @HostListener('window:resize') onResize(): void {
    const { innerWidth } = window;
    if (this.activeHamburger && innerWidth > 768) {
      this.activeHamburger = false;
      this.checkScroll();
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
